import React, { useMemo, useRef, useState } from "react";

import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

import { SingleSelectInput, Table, TableRef, Text } from "@performyard/py-components";
import { InputBase } from "@performyard/py-components/src/components/atoms/InputBase";

import {
	CycleBudgetsColDefs,
	cycleBudgetsGridViewportHeightPx,
	cycleBudgetsHeaderHeightPx,
	cycleBudgetsRowHeightPx,
	getCycleBudgetsTableTheme,
} from "./CycleBudgetsTableUtils";

import { type CycleBudgetOverviewTableRow } from "../utils";

type QuickFilterValue = "not-submitted" | "submitted" | "approved";

const quickFilterValueToRowStatus: Record<QuickFilterValue, string> = {
	"not-submitted": "Not Submitted",
	submitted: "Submitted",
	approved: "Approved",
};

type CycleBudgetsWidgetProps = {
	enhancedManagerGroups: CycleBudgetOverviewTableRow[];
};

const options: { label: string; value: QuickFilterValue }[] = [
	{ label: "Not Submitted", value: "not-submitted" },
	{ label: "Submitted", value: "submitted" },
	{ label: "Approved", value: "approved" },
];

export const CycleBudgetsWidget: React.FC<CycleBudgetsWidgetProps> = ({ enhancedManagerGroups }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [quickFilter, setQuickFilter] = useState<QuickFilterValue | null>(null);
	const gridRef = useRef<TableRef<CycleBudgetOverviewTableRow> | null>(null);

	const filteredRowData = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		let rows = enhancedManagerGroups;
		if (query.length > 0) {
			rows = rows.filter(
				(row) =>
					row.managerName.toLowerCase().includes(query) ||
					(row.actingManagerName?.toLowerCase().includes(query) ?? false)
			);
		}
		if (quickFilter != null) {
			const statusLabel = quickFilterValueToRowStatus[quickFilter];
			rows = rows.filter((row) => row.status === statusLabel);
		}
		return rows;
	}, [enhancedManagerGroups, quickFilter, searchQuery]);

	return (
		<div className="mt-6 box-border flex w-full flex-col gap-2 rounded-lg border border-solid border-neutral-2 p-4">
			<div className="flex w-full min-w-0 shrink-0 flex-row items-center justify-between gap-4">
				<Text size="paragraph-3" weight="semi-bold">
					Cycle Budgets
				</Text>
				<div className="flex shrink-0 flex-row items-center gap-2">
					<InputBase
						leftIcon={faMagnifyingGlass}
						placeholder="Search by manager..."
						value={searchQuery}
						onChange={setSearchQuery}
						dangerouslySetClassName="w-[200px] shrink-0"
						size="small"
					/>
					<div className="w-[150px] shrink-0">
						<SingleSelectInput<QuickFilterValue>
							placeholder="Quick Filters"
							size="small"
							options={options}
							value={quickFilter ?? undefined}
							onChange={setQuickFilter}
							isClearable
						/>
					</div>
				</div>
			</div>
			<div
				className="w-full min-w-0 [&_.ag-root-wrapper]:border-transparent [&_.cycle-budgets-actions-header_.ag-header-cell-label]:justify-center"
				style={{ height: cycleBudgetsGridViewportHeightPx }}
			>
				<Table<CycleBudgetOverviewTableRow>
					ref={gridRef}
					rowData={filteredRowData}
					columnDefs={CycleBudgetsColDefs}
					theme={getCycleBudgetsTableTheme()}
					animateRows={true}
					getRowId={(params) => params.data._id}
					rowHeight={cycleBudgetsRowHeightPx}
					headerHeight={cycleBudgetsHeaderHeightPx}
				/>
			</div>
		</div>
	);
};
